<main class="page-shell profile-page">
  <section *ngIf="loading" class="card state">Loading profile...</section>
  <section *ngIf="error" class="card state status-error">{{ error }}</section>

  <ng-container *ngIf="!loading && !error && profile as p">
    <section class="card profile-hero">
      <div class="hero-main">
        <div class="avatar-shell">
          <img
            *ngIf="avatarPreviewUrl || p.avatar; else avatarFallback"
            [src]="avatarPreviewUrl || p.avatar"
            alt="Profile avatar"
          />
          <ng-template #avatarFallback>
            <span>{{ getInitials(p.name) }}</span>
          </ng-template>
        </div>
        <div class="hero-text">
          <h1>{{ p.name }}</h1>
          <p class="muted">{{ p.email || p.phone || "No contact info" }}</p>
          <div class="hero-tags">
            <span>Level {{ p.level }}</span>
            <span>{{ p.xp }} XP</span>
            <span>Streak {{ p.streak }}</span>
          </div>
        </div>
      </div>
      <div class="stats-grid">
        <article class="stat">
          <label>Solved</label>
          <strong>{{ p.solvedLevels }}</strong>
        </article>
        <article class="stat">
          <label>Accuracy</label>
          <strong>{{ ((p.solvedLevels / p.totalLevels) * 100) | number: "1.0-1" }}%</strong>
        </article>
        <article class="stat">
          <label>Streak</label>
          <strong>{{ p.streak }}</strong>
        </article>
        <article class="stat">
          <label>XP</label>
          <strong>{{ p.xp }}</strong>
        </article>
      </div>
    </section>

    <section class="card profile-edit">
      <div class="edit-grid">
        <form [formGroup]="profileForm" (ngSubmit)="saveProfile()" class="edit-form profile-form">
          <h3>Profile details</h3>
          <div class="avatar-upload">
            <div class="avatar-shell large">
              <img
                *ngIf="avatarPreviewUrl || p.avatar; else uploadFallback"
                [src]="avatarPreviewUrl || p.avatar"
                alt="Profile avatar"
              />
              <ng-template #uploadFallback>
                <span>{{ getInitials(p.name) }}</span>
              </ng-template>
            </div>
            <div class="avatar-actions">
              <p class="label">Profile photo</p>
              <p class="muted small">PNG, JPG, WEBP or GIF · max 2MB</p>
              <div class="avatar-buttons">
                <input
                  #avatarInput
                  type="file"
                  class="avatar-input"
                  accept="image/*"
                  (change)="onAvatarSelected($event)"
                />
                <button
                  type="button"
                  class="btn-secondary"
                  [disabled]="avatarUploading"
                  (click)="avatarInput.click()"
                >
                  {{ avatarUploading ? "Uploading..." : "Upload photo" }}
                </button>
                <button
                  type="button"
                  class="btn-ghost"
                  [disabled]="!p.avatar || avatarUploading"
                  (click)="removeAvatar()"
                >
                  Remove
                </button>
              </div>
              <p class="message" *ngIf="avatarMessage">{{ avatarMessage }}</p>
              <p class="error" *ngIf="avatarError">{{ avatarError }}</p>
            </div>
          </div>
          <label>
            Name
            <input type="text" formControlName="name" placeholder="Your name" />
          </label>
          <label>
            Phone number
            <input type="text" formControlName="phone" placeholder="+91 98765 43210" />
          </label>
          <button type="submit" class="btn-primary" [disabled]="savingProfile">Save profile</button>
          <p class="message" *ngIf="profileMessage">{{ profileMessage }}</p>
          <p class="error" *ngIf="profileError">{{ profileError }}</p>
        </form>

        <form [formGroup]="passwordForm" (ngSubmit)="updatePassword()" class="edit-form">
          <h3>Update password</h3>
          <label>
            Current password
            <input type="password" formControlName="currentPassword" placeholder="Current password" />
          </label>
          <label>
            New password
            <input type="password" formControlName="newPassword" placeholder="New password" />
          </label>
          <label>
            Confirm password
            <input type="password" formControlName="confirmPassword" placeholder="Confirm password" />
          </label>
          <p class="hint">
            Password must be 8+ chars with uppercase, lowercase, number, and symbol.
          </p>
          <button type="submit" class="btn-primary" [disabled]="savingPassword">Update password</button>
          <p class="message" *ngIf="passwordMessage">{{ passwordMessage }}</p>
          <p class="error" *ngIf="passwordError">{{ passwordError }}</p>
        </form>
      </div>
    </section>

    <section class="card progress-card">
      <div class="row-title">
        <h3>Progress</h3>
        <span>{{ p.solvedLevels }} / {{ p.totalLevels }}</span>
      </div>
      <div class="progress-track">
        <div class="progress-fill" [style.width.%]="(p.solvedLevels / p.totalLevels) * 100"></div>
      </div>
    </section>

    <section class="insights-grid" *ngIf="insights as i">
      <article class="card insight-card">
        <h3>Strong Areas</h3>
        <ul>
          <li *ngFor="let item of i.strongAreas">
            {{ item.topic }} <span>{{ item.percent }}%</span>
          </li>
        </ul>
      </article>

      <article class="card insight-card">
        <h3>Focus Areas</h3>
        <ul>
          <li *ngFor="let item of i.weakAreas">
            {{ item.topic }} <span>{{ item.percent }}%</span>
          </li>
        </ul>
      </article>

      <article class="card insight-card">
        <h3>Recommended Next</h3>
        <ul>
          <li *ngFor="let item of i.recommendations.slice(0, 5)">
            #{{ item.levelNumber }} {{ item.title }}
          </li>
        </ul>
      </article>
    </section>

    <section class="card achievements-card">
      <h3>Achievements</h3>
      <div class="achievement-grid" *ngIf="p.achievements.length > 0; else noAchievements">
        <article class="achievement" *ngFor="let achievement of p.achievements">
          <h4>{{ achievement.name }}</h4>
          <p>{{ achievement.description }}</p>
          <code>{{ achievement.key }}</code>
        </article>
      </div>
      <ng-template #noAchievements>
        <p class="muted">No achievements unlocked yet.</p>
      </ng-template>
    </section>

    <section class="activity-grid">
      <article class="card activity-card">
        <h3>Recent Activity</h3>
        <table *ngIf="p.activityTimeline.length > 0; else noActivity">
          <thead>
            <tr>
              <th>Problem</th>
              <th>Status</th>
              <th>Runtime</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of p.activityTimeline.slice(0, 12)">
              <td>{{ item.problemTitle }}</td>
              <td [class.status-accepted]="item.status === 'ACCEPTED'">{{ item.status }}</td>
              <td>{{ item.executionTime ?? "-" }} ms</td>
              <td>{{ item.createdAt | date: "short" }}</td>
            </tr>
          </tbody>
        </table>
        <ng-template #noActivity>
          <p class="muted">No recent submissions yet.</p>
        </ng-template>
      </article>

      <article class="card favorites-card">
        <h3>Favorite Problems</h3>
        <ul *ngIf="p.favoriteSolutions.length > 0; else noFavorites">
          <li *ngFor="let item of p.favoriteSolutions">
            <span>#{{ item.id }} {{ item.title }}</span>
            <small>{{ item.topic }} · {{ item.difficulty }}</small>
          </li>
        </ul>
        <ng-template #noFavorites>
          <p class="muted">Bookmark problems to build your personal collection.</p>
        </ng-template>
      </article>
    </section>
  </ng-container>
</main>
